/**
 * @file HTTP 客户端（axios）
 * @description 统一 baseURL、Bearer 鉴权与业务响应解包；供 `src/api` 各模块调用。
 *
 * 约定（对齐 RuoYi-Vue-Plus / plus-ui 字段）：
 * - 成功：`code === 200`（缺省 code 视为成功）
 * - 文案字段：`msg`
 * - 详情 / 无分页列表：数据在 `data`
 * - 分页列表：顶层 `rows` + `total`（与 code/msg 同级）
 *
 * @whenToUse 所有后端 JSON API 请求入口。
 * @whenNotToUse 静态资源、文件流下载、非本约定的第三方接口（可直接用 axios/fetch）。
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from 'axios'
import { clearAuthToken, getAuthToken } from '@/lib/auth.ts'
import {
  ENCRYPT_HEADER,
  decryptBase64,
  decryptWithAes,
  encryptBase64,
  encryptWithAes,
  generateAesKey,
  rsaDecrypt,
  rsaEncrypt,
} from '@/lib/crypto.ts'

/** 接口加密开关（与后端必须一致）；请求级还需传 `encrypt: true` 才真正加密 */
const encryptEnabled = import.meta.env.VITE_APP_ENCRYPT === 'true'

declare module 'axios' {
  interface AxiosRequestConfig {
    /** 请求体加密标记；仅开启时对 post/put 请求体加密（当前仅登录使用） */
    encrypt?: boolean
  }
}

/** 业务成功码 */
export const API_SUCCESS_CODE = 200

/** 统一业务响应体（详情 / 无分页） */
export type ApiResponse<T> = {
  /** 业务码；200 表示成功 */
  code: number
  /** 业务数据 */
  data: T
  /** 提示文案 */
  msg: string
}

/** 分页列表业务响应体（rows/total 与 code/msg 同级） */
export type ApiPageResponse<T> = {
  /** 业务码；200 表示成功 */
  code: number
  /** 提示文案 */
  msg: string
  /** 当前页数据 */
  rows: T[]
  /** 总条数 */
  total: number
}

/** 分页列表解包结果 */
export type ApiPageResult<T> = {
  /** 当前页数据 */
  rows: T[]
  /** 总条数 */
  total: number
}

/** 业务失败或 HTTP 失败 */
export class ApiError extends Error {
  /** 业务码，或 HTTP 状态码兜底 */
  code: number
  /** HTTP 状态码（网络层失败时可能为空） */
  status: number | undefined

  /**
   * @param message 错误文案
   * @param code 业务码或状态码
   * @param status HTTP 状态码
   */
  constructor(message: string, code: number, status?: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

/**
 * 校验业务码；失败抛 ApiError。
 * @param code 业务码
 * @param msg 提示文案
 * @param status HTTP 状态码
 */
function assertSuccess(code: number, msg: string | undefined, status: number) {
  const resolved = code || API_SUCCESS_CODE
  if (resolved === 401) {
    clearAuthToken()
  }
  if (resolved !== API_SUCCESS_CODE) {
    throw new ApiError(msg || '请求失败', resolved, status)
  }
}

/**
 * 解包详情 / 无分页响应，返回 `data`。
 * @param payload 后端响应体
 * @param status HTTP 状态码
 */
function unwrapData<T>(payload: ApiResponse<T>, status: number): T {
  assertSuccess(payload.code, payload.msg, status)
  return payload.data
}

/**
 * 解包分页响应，返回 `{ rows, total }`。
 * @param payload 后端响应体
 * @param status HTTP 状态码
 */
function unwrapPage<T>(
  payload: ApiPageResponse<T>,
  status: number,
): ApiPageResult<T> {
  assertSuccess(payload.code, payload.msg, status)
  return { rows: payload.rows, total: payload.total }
}

/** 全局 axios 实例（需要原始响应时也可直接用） */
export const client: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_API || '/api',
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': 'zh-CN',
    'Content-Language': 'zh_CN',
  },
})

client.interceptors.request.use((config) => {
  const token = getAuthToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  config.headers.clientid = import.meta.env.VITE_APP_CLIENT_ID
  if (
    encryptEnabled &&
    config.encrypt === true &&
    (config.method === 'post' || config.method === 'put') &&
    config.data != null &&
    !(config.data instanceof FormData)
  ) {
    // 随机 AES key 加密请求体；key 本体经 RSA 加密放 encrypt-key 头
    const aesKey = generateAesKey()
    const encryptedKey = rsaEncrypt(encryptBase64(aesKey))
    if (typeof encryptedKey === 'string') {
      config.headers[ENCRYPT_HEADER] = encryptedKey
      const json =
        typeof config.data === 'object'
          ? JSON.stringify(config.data)
          : config.data
      config.data = encryptWithAes(json, aesKey)
    }
  }
  return config
})

client.interceptors.response.use(
  (response) => {
    if (encryptEnabled) {
      const keyStr = response.headers[ENCRYPT_HEADER]
      if (typeof keyStr === 'string' && keyStr !== '') {
      const decryptedKey = rsaDecrypt(keyStr)
      if (typeof decryptedKey === 'string') {
        const aesKey = decryptBase64(decryptedKey)
        response.data = JSON.parse(decryptWithAes(response.data, aesKey))
      }
      }
    }
    return response
  },
  (error: AxiosError<ApiResponse<null>>) => {
    if (error.response?.status === 401) {
      clearAuthToken()
    }

    const status = error.response?.status
    const code = error.response?.data?.code ?? status ?? -1
    const message =
      error.response?.data?.msg || error.message || '网络异常'

    return Promise.reject(new ApiError(message, code, status))
  },
)

/**
 * GET，返回解包后的 `data`（详情 / 无分页列表）。
 * @param url 相对路径
 * @param config axios 配置（如 params）
 */
export async function get<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await client.get<ApiResponse<T>>(url, config)
  return unwrapData(response.data, response.status)
}

/**
 * GET 分页列表，返回 `{ rows, total }`。
 * @param url 相对路径
 * @param config axios 配置（如 params：pageNum / pageSize）
 */
export async function getPage<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiPageResult<T>> {
  const response = await client.get<ApiPageResponse<T>>(url, config)
  return unwrapPage(response.data, response.status)
}

/**
 * POST，返回解包后的 `data`。
 * @param url 相对路径
 * @param data 请求体
 * @param config axios 配置
 */
export async function post<T>(
  url: string,
  data?: AxiosRequestConfig['data'],
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await client.post<ApiResponse<T>>(url, data, config)
  return unwrapData(response.data, response.status)
}

/**
 * PUT，返回解包后的 `data`。
 * @param url 相对路径
 * @param data 请求体
 * @param config axios 配置
 */
export async function put<T>(
  url: string,
  data?: AxiosRequestConfig['data'],
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await client.put<ApiResponse<T>>(url, data, config)
  return unwrapData(response.data, response.status)
}

/**
 * PATCH，返回解包后的 `data`。
 * @param url 相对路径
 * @param data 请求体
 * @param config axios 配置
 */
export async function patch<T>(
  url: string,
  data?: AxiosRequestConfig['data'],
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await client.patch<ApiResponse<T>>(url, data, config)
  return unwrapData(response.data, response.status)
}

/**
 * DELETE，返回解包后的 `data`。
 * @param url 相对路径
 * @param config axios 配置
 */
export async function del<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await client.delete<ApiResponse<T>>(url, config)
  return unwrapData(response.data, response.status)
}
